

import { io } from 'socket.io-client';
import {
    processUpdate,
    processTwin,
    processData,
    processMessages,
    processMeta,
    processOta,
    processMap
} from './events';
import { init as initCommands } from './commands';

let socket = null;
let _onReconnect = null;  // callback to refetch data after a reconnect

export const connectSocket = function(token, dispatch, getAccessTokenSilently, onReconnect) {

    if (socket) {
        console.log('socket already connected');
        return socket;
    }

    _onReconnect = onReconnect;

    socket = io({ query: { token }, autoConnect: false });
    socket.connect();

    let wasDisconnected = false;

    // Collapse the burst of resume triggers — pageshow + visibilitychange,
    // plus the reconnect that follows a dead-socket resume — into one refetch.
    let lastRefetchAt = 0;
    const REFETCH_COOLDOWN_MS = 3000;
    const guardedRefetch = () => {
        const now = Date.now();
        if (now - lastRefetchAt < REFETCH_COOLDOWN_MS) {
            console.log('refetch suppressed (within cooldown)');
            return;
        }
        lastRefetchAt = now;
        _onReconnect && _onReconnect();
    };

    socket.on('connect', () => {
        console.log('connected:', socket.id);
        if (wasDisconnected) {
            wasDisconnected = false;
            console.log('socket reconnected');
            guardedRefetch();
        }
    });

    socket.on('error',         (err) => console.log('socket error:', err));
    socket.on('connect_error', (err) => console.log(`connect_error due to ${err.message}`));
    socket.on('disconnect', () => {
        console.log('socket disconnected', socket.id);
        wasDisconnected = true;
        // Socket.IO's built-in reconnection (on by default) handles retry.
    });

    socket.on('update',   (packet)   => processUpdate(packet, dispatch));
    socket.on('twin',     (packet)   => processTwin(packet, dispatch));
    socket.on('data',     (packet)   => processData(packet, dispatch, getAccessTokenSilently));
    socket.on('messages', (messages) => processMessages(messages, dispatch));
    socket.on('meta',     (packet)   => processMeta(packet, dispatch));
    socket.on('ota',      (packet)   => processOta(packet, dispatch));
    socket.on('map',      (packet)   => processMap(packet, dispatch, getAccessTokenSilently));

    initCommands(socket);

    // ─── Page Visibility / Resumption ──────────────────────────────────────
    // Mobile browsers and bfcache restores need both events to be reliable.
    const handleResume = () => {
        if (document.visibilityState !== 'visible') return;

        // Fresh data NOW — this is plain HTTP and does not depend on the
        // socket being alive. Running it unconditionally (through the guard)
        // is what removes the ~10s wait, since we no longer block the refetch
        // on the reconnect handshake.
        console.log('page resumed — refetching data');
        guardedRefetch();

        // Separately, bring the live feed back. Let the socket reconnect on
        // its own track; the refetch above has already filled the gap.
        if (!socket.connected) {
            console.log('socket dead on resume — reconnecting');
            wasDisconnected = true;
            socket.connect();
        }
    };

    document.addEventListener('visibilitychange', handleResume);

    // pageshow fires on bfcache restores (common on mobile Safari)
    // where visibilitychange may not fire at all.
    window.addEventListener('pageshow', (e) => {
        if (e.persisted) {
            console.log('bfcache restore — triggering resume');
            handleResume();
        }
    });

    return socket;
};

export const disconnectSocket = function() {
    if (socket) {
        socket.disconnect();
        console.log('socket disconnected');
        socket = null;
    } else {
        console.log('socket is not connected');
    }
};

export { emitCommand, onFeedback } from './commands';